import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Link, useNavigate } from "react-router-dom";
import useDropdownMenu from "../hooks/useDropdownMenu";
import { useStudent } from "../context/StudentContext";
import { MensajesContext } from "../context/MensajesContext";
import { API_URL } from "../config";
import { clearSessionKeys } from "../utils/sessionKeys";
import { fetchSessionStatus } from "../utils/sessionAuth";
import { getHomeRouteByRole, isAlumno } from "../utils/roles";
import HeaderBrandLogo from "./HeaderBrandLogo";
import ConstrainedSelect from "./ConstrainedSelect";
import { getMessage } from "../data/locales";
import {
  PROFILE_MENU,
  announceOverlayMenuOpen,
  onOtherOverlayMenuOpen,
} from "../utils/exclusiveOverlayMenus";
import "./SidebarUser.css";

function buildUnreadIndicatorPath(unreadByContact) {
  const firstUnreadRow = Array.isArray(unreadByContact)
    ? unreadByContact.find((row) => Number(row?.count) > 0)
    : null;
  const firstUnreadContactId = String(firstUnreadRow?.contactoId || "").trim();
  if (!firstUnreadContactId) {
    return "";
  }

  const firstUnreadScope = String(firstUnreadRow?.cursoId || firstUnreadRow?.scope || firstUnreadRow?.filtro || "").trim();
  const firstUnreadRole = String(firstUnreadRow?.rolGlobal || firstUnreadRow?.rol || "").trim().toLowerCase();
  const unreadIndicatorParams = new URLSearchParams();
  unreadIndicatorParams.set("contacto", firstUnreadContactId);
  if (firstUnreadScope) {
    unreadIndicatorParams.set("curso", firstUnreadScope);
  } else if (firstUnreadRole === "director" || firstUnreadRole === "instructor") {
    unreadIndicatorParams.set("scope", "administradores");
  } else if (firstUnreadRole === "alumno") {
    unreadIndicatorParams.set("scope", "usuarios");
  }

  return `/mensajeria-interna?${unreadIndicatorParams.toString()}`;
}

const HeaderPrivado = ({ showHamburger = false, sidebarOpen = false, onToggleSidebar }) => {
  const navigate = useNavigate();
  const { menuAbierto, toggleMenu, closeMenu, menuRef } = useDropdownMenu();
  const { courses = [], activeCourse, setActiveCourse } = useStudent();
  const { totalUnread = 0, unreadByContact = [] } = useContext(MensajesContext) || {};

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      console.log("Error cerrando sesión:", e);
    }
    clearSessionKeys();
    navigate("/login", { replace: true });
  };

  const [rutaInicio, setRutaInicio] = useState("/");
  const [userRole, setUserRole] = useState(null);
  const [isRoleResolved, setIsRoleResolved] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    fetchSessionStatus(controller.signal)
      .then((session) => {
        if (controller.signal.aborted) return;
        if (session.authenticated && session.usuario) {
          setUserRole(session.usuario.rol);
          setRutaInicio(getHomeRouteByRole(session.usuario.rol) || "/");
        }
        setIsRoleResolved(true);
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setIsRoleResolved(true);
        }
      });
    return () => controller.abort();
  }, []);

  useEffect(() => onOtherOverlayMenuOpen(PROFILE_MENU, closeMenu), [closeMenu]);

  const handleHamburgerClick = () => {
    if (menuAbierto) {
      closeMenu();
    }
    onToggleSidebar?.();
  };

  const handleProfileToggle = () => {
    if (!menuAbierto) {
      announceOverlayMenuOpen(PROFILE_MENU);
    }
    toggleMenu();
  };

  const handleCourseChange = (courseId) => {
    const selected = courses.find((c) => c._id === courseId);
    if (selected) {
      setActiveCourse(selected);

      globalThis.dispatchEvent(new CustomEvent("courseChanged", {
        detail: { courseId: selected._id },
      }));
      navigate("/usuario/campus");
    }
  };

  const showCourseSelector = isRoleResolved && isAlumno(userRole);
  const showStaffUnreadIndicator = isRoleResolved && !isAlumno(userRole) && totalUnread > 0;
  const unreadIndicatorPath = buildUnreadIndicatorPath(unreadByContact);
  const messagingIndicatorPath = unreadIndicatorPath || "/mensajeria-interna";
  const headerClassName = showCourseSelector ? "side-header" : "side-header side-header--staff";

  return (
    <div className={headerClassName}>
      <div className="side-header-brand">
        {showHamburger && (
          <button
            className="side-hamburger"
            onClick={handleHamburgerClick}
            aria-expanded={sidebarOpen}
            aria-label="Abrir o cerrar menú lateral"
          >
            ☰
          </button>
        )}
        <HeaderBrandLogo linkTo={isRoleResolved ? rutaInicio : undefined} />
      </div>

      {showCourseSelector ? (
        <div className="side-middle-container">
          {courses.length > 1 ? (
            <ConstrainedSelect
              className="course-selector"
              value={activeCourse?._id || ""}
              onChange={handleCourseChange}
              ariaLabel={getMessage("student.header.selectCourse")}
              options={courses.map((course) => ({
                value: course._id,
                label: course.titulo || course.nombre || getMessage("student.header.unnamedCourse"),
              }))}
            />
          ) : (
            <h2 className="side-header-course-title">
              {courses.length === 0
                ? getMessage("student.header.noCourses")
                : (activeCourse?.titulo || activeCourse?.nombre || getMessage("student.header.unnamedCourse"))}
            </h2>
          )}
        </div>
      ) : null}

      <div
        className="side-user-menu"
        ref={menuRef}
      >
        {showStaffUnreadIndicator && (
          <Link
            to={messagingIndicatorPath}
            className="side-message-indicator"
            aria-label={`${totalUnread} mensajes sin leer. Ir a mensajería`}
          >
            <span className="side-message-indicator__icon" aria-hidden="true">✉</span>
            <span className="side-message-indicator__count">
              {totalUnread > 99 ? "99+" : totalUnread}
            </span>
          </Link>
        )}
        <button
          type="button"
          className="user-profile-trigger"
          onClick={handleProfileToggle}
          aria-haspopup="menu"
          aria-expanded={menuAbierto}
          aria-label="Menú de perfil"
        >
          <img
            src="/logos/LOGO_PERFIL.png"
            alt=""
            className="side-user-icon ui-integrated-image"
            draggable="false"
            onContextMenu={(e) => e.preventDefault()}
          />
        </button>
        {menuAbierto && (
          <div className="side-dropdown">
            <Link to="/Perfil" className="side-dropdown-item">Perfil</Link>
            <button type="button" className="side-dropdown-item" onClick={handleLogout}>
              Cerrar Sesión
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

HeaderPrivado.propTypes = {
  showHamburger: PropTypes.bool,
  sidebarOpen: PropTypes.bool,
  onToggleSidebar: PropTypes.func,
};

export default HeaderPrivado;
